/**
 * "What's new" state — OMN-368.
 *
 * One provider so the slide-over panel is rendered a single time (the Sidebar,
 * which hosts the entry-point button, is mounted twice — desktop + mobile
 * drawer — so panel state must live above it).
 *
 * Content loads from public Storage at runtime (see whatsNewData), falling back
 * to the bundled copy. Unread = newest entry date vs `lastSeenChangelog` in
 * localStorage; opening the panel marks everything current as seen. Unread is
 * DERIVED (not stored) so it updates correctly once the remote fetch resolves.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ChangelogEntry, fetchRemoteEntries, getFallbackEntries } from './whatsNewData';

const STORAGE_KEY = 'lastSeenChangelog';

function readLastSeen(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // private mode / storage disabled — treat as "never seen"
  }
}

function writeLastSeen(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Non-fatal: the dot may reappear next session, but nothing breaks.
  }
}

interface WhatsNewContextValue {
  entries: ChangelogEntry[];
  hasUnread: boolean;
  isOpen: boolean;
  /** Open the panel and mark all current entries as seen. */
  open: () => void;
  close: () => void;
}

const WhatsNewContext = createContext<WhatsNewContextValue | undefined>(undefined);

export const WhatsNewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Start with the bundled copy so the panel is never empty; swap in the live
  // Storage content once it arrives.
  const [entries, setEntries] = useState<ChangelogEntry[]>(() => getFallbackEntries());
  const [lastSeen, setLastSeen] = useState<string | null>(() => readLastSeen());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchRemoteEntries().then((remote) => {
      if (!cancelled && remote) setEntries(remote);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const latest = entries[0]?.date ?? '';
  // Derived: recomputes when the remote entries land or after open() bumps lastSeen.
  const hasUnread = latest !== '' && (lastSeen === null || lastSeen < latest);

  const open = useCallback(() => {
    setIsOpen(true);
    if (latest) {
      writeLastSeen(latest);
      setLastSeen(latest);
    }
  }, [latest]);

  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ entries, hasUnread, isOpen, open, close }),
    [entries, hasUnread, isOpen, open, close],
  );

  return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
};

export function useWhatsNew(): WhatsNewContextValue {
  const ctx = useContext(WhatsNewContext);
  if (!ctx) {
    throw new Error('useWhatsNew must be used within a WhatsNewProvider');
  }
  return ctx;
}
